import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

export const authApi = {
  getMe: () => api.get<User>('/api/auth/me').then((r) => r.data),
  updateProfile: (data: { name?: string; uiLanguage?: string; accentColor?: string }) =>
    api.patch<User>('/api/auth/me', data).then((r) => r.data),
  /** Polish step 3 — change password for email-auth users. */
  changePassword: (currentPassword: string, newPassword: string) =>
    api
      .post<{ message: string }>('/api/auth/change-password', { currentPassword, newPassword })
      .then((r) => r.data),
  register: (name: string, email: string, password: string, inviteCode?: string) =>
    api
      .post<{ message: string }>('/api/auth/register', { name, email, password, inviteCode })
      .then((r) => r.data),
  login: (email: string, password: string) =>
    api
      .post<{
        token: string;
        user: {
          id: string;
          name: string;
          email: string;
          avatarUrl: string | null;
          onboardingCompleted: boolean;
        };
      }>('/api/auth/login', { email, password })
      .then((r) => r.data),
  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/api/auth/forgot-password', { email }).then((r) => r.data),
  resendVerification: (email: string) =>
    api.post<{ sent: boolean }>('/api/auth/resend-verification', { email }).then((r) => r.data),
  resetPassword: (token: string, password: string) =>
    api
      .post<{ message: string }>('/api/auth/reset-password', { token, password })
      .then((r) => r.data),
};

export interface DeletionStatusResponse {
  status: 'none' | 'pending' | 'completed';
  scheduledFor?: string | null;
  confirmationCode?: string | null;
}

export interface DeletionRequestResponse {
  confirmationCode: string;
  status: string;
  scheduledDeleteAt: string | null;
}

/** GDPR endpoints — export, delete, status. */
export const accountApi = {
  status: () => api.get<DeletionStatusResponse>('/api/account/delete/status').then((r) => r.data),
  requestDeletion: () =>
    api.post<DeletionRequestResponse>('/api/account/delete').then((r) => r.data),
  cancelDeletion: (confirmationCode: string) =>
    api
      .delete<DeletionRequestResponse>(`/api/account/delete/${confirmationCode}`)
      .then((r) => r.data),
  /** Triggers a browser download of the full JSON export. */
  exportData: async () => {
    const res = await api.get('/api/account/export', { responseType: 'blob' });
    const blob = res.data as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().substring(0, 10);
    a.href = url;
    a.download = `merkflow-data-${date}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

export const onboardingApi = {
  getStatus: () =>
    api
      .get<{
        currentStep: number;
        accountType: string | null;
        workspaceId: string | null;
        workspaceName: string | null;
        brandId: string | null;
        brandName: string | null;
        selectedPlan: string | null;
        completed: boolean;
      }>('/api/onboarding/status')
      .then((r) => r.data),
  setAccountType: (accountType: string) =>
    api.post('/api/onboarding/step/account-type', { accountType }).then((r) => r.data),
  setupWorkspace: (workspaceName: string, brandName: string, brandWebsite?: string) =>
    api
      .post('/api/onboarding/step/workspace', { workspaceName, brandName, brandWebsite })
      .then((r) => r.data),
  selectPlan: (selectedPlan: string) =>
    api.post('/api/onboarding/step/plan', { selectedPlan }).then((r) => r.data),
  complete: () => api.post('/api/onboarding/complete').then((r) => r.data),
};
